import { faArrowRight, faTrashCan } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useEffect, useState, useCallback, useRef, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation } from "react-router-dom";
import AppBar from "../../common/AppBar";
import SearchBarWithFilter from "../../components/SearchBarWithFilter";
import { BASE } from "../../config";
import {
  createModule,
  getAllModules,
  getAllUnlinkModules,
  getModuleById,
  updateModule,
} from "../../redux/moduleSlice";
import {
  getAllFlowchartParts,
  getAllModuleParts,
  getAllProcedureParts,
} from "../../redux/partSlice";
import {
  getProcedureRevisionsForPart,
  getRevisionsForPart,
} from "../../redux/procedureRevisionSlice";

import { getAllTypes } from "../../redux/typeSlice";
import { getAllUnits } from "../../redux/unitSlice";
import { formatName, getCurrentStatus } from "../../util/AppUtils.jsx";
import { uploadJsonToS3 } from "../../util/AppUtils.js";
import { cleanBaselineHtml } from "../../util/lexical";
import {
  getGlobalAppViewMode,
  setGlobalAppViewMode,
} from "../../util/appViewModeStorage";
import {
  isRevisionWorkflowActor,
  isViewerSarOnOlderSameLetterReleasedRevision,
} from "../../util/trainingLogStatus";
import "./AddModule.css";
import { getAuditTrailData } from "../../util/getAuditTrailData.js";
import PartsPopup from "../../common/AppSpecificConpnents/PartsTable/PartsPopup.js";
import RenderHtml from "../../common/AppSpecificConpnents/PartsTable/RenderHtml.js";
import TableOfDocuments from "./TableOfDocuments.js";
import { showSnackbar } from "../../redux/snackbarSlice.js";
import Input from "./Input.js";
import { SortTableData } from "../../common/AppTable/SortTableData.js";
import ProjectIdPopup from "./ProjectId.jsx/ProjectIdPopup.js";
import { useNavigate } from "react-router-dom";
import { useUnsavedChangesBlocker } from "../../hooks/useUnsavedChangesBlocker";
import Loader from "../../common/Loader.js";
import InlineLoader from "../../common/InlineLoader";
import { SaveModal } from "../../common/Modals";
import ModuleForm from "./ModuleForm.js";
import InputWithPen from "../../common/Inputs/InputWithPen.js";
import SelectRevisionModal from "./SelectRevisionModal";
import { getAllVersionHistories } from "../../redux/versionHistorySlice";
import Popup from "../../common/Popup/Popup.js";
import AppTable from "../../common/AppTable/AppTable.js";
import ExportCsvBtn from "../../common/AppSpecificConpnents/ExportCsvBtn.js";
import FilterDropdown from "../../common/AppSpecificConpnents/PartsTable/FilterDropdown.js";
import Images from "../../assests/imgs/Imges.js";
import {
  TableActionGroup,
  SaveRowButton,
  CancelRowButton,
  EditRowButton,
  DeleteRowButton,
  SelectRowButton,
} from "../../common/Buttons";
import { buildPermissionContext } from "../../util/permissions";
import ProductsLiveModeAppBar from "../Products/ProductsLiveModeAppBar";

/** Reads API / navigation module payload for “hide from live mode”. */
const getHideFromLiveModeFromModule = (mod) => {
  if (!mod) return false;
  const v =
    mod.is_hide_from_live_mode ??
    mod.hide_from_live_mode ??
    mod.hide_from_live;
  if (v === true || v === "true" || v === 1 || v === "1") return true;
  return false;
};

const normalizeShortDescriptionForDirtyCheck = (val) => {
  if (val === null || val === undefined) return "";
  const s = String(val)
    .replace(/\r\n/g, "\n")
    .replace(/\s+/g, " ")
    .trim();
  if (s === "") return "";
  const compact = s.replace(/>\s+</g, "><");
  if (
    compact === "<p></p>" ||
    compact === "<p><br></p>" ||
    compact === "<p><br/></p>" ||
    compact === "<p><br /></p>"
  ) {
    return "";
  }
  return compact;
};

/**
 * Converts a raw short_description value (plain text OR existing HTML) into
 * clean HTML suitable for the Lexical rich text editor.
 *   - Already-HTML values (contain at least one HTML tag) are passed through
 *     cleanBaselineHtml to strip editor-specific classes/attributes.
 *   - Plain-text values are HTML-escaped, then double-newlines become paragraph
 *     breaks and single newlines become <br> tags.
 */
const convertShortDescToHtml = (raw) => {
  if (!raw) return "";
  const isHtml = /<[a-z][\s\S]*>/i.test(raw);
  if (isHtml) {
    return cleanBaselineHtml(raw);
  }
  const normalized = raw.replace(/\r\n\r\n/g, "\n\n").replace(/\r\n/g, "\n");
  const escaped = normalized
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");
  const paragraphs = escaped
    .split(/\n\n/)
    .map((para) => `<p>${para.replace(/\n/g, "<br>")}</p>`)
    .join("");
  return paragraphs || `<p>${escaped}</p>`;
};

const ADD_MODULE_VIEW_MODE_PREFIX = "addModule_isViewMode_";
/** Set in Document back navigation; survives when production strips `location.state`. */
const ADD_MODULE_PENDING_DOC_VIEW_MODE = "addModule_pending_doc_view_mode";
/** Persist preview restore key so refresh can recover Add Module context. */
const ADD_MODULE_PREVIEW_RESTORE_KEY = "addModule_restore_key";

function readPersistedAddModuleViewMode(moduleId) {
  if (moduleId == null || moduleId === "") return null;
  try {
    const raw = sessionStorage.getItem(
      `${ADD_MODULE_VIEW_MODE_PREFIX}${moduleId}`,
    );
    if (raw === "true") return true;
    if (raw === "false") return false;
    return null;
  } catch {
    return null;
  }
}

function persistAddModuleViewMode(moduleId, isViewMode) {
  if (moduleId == null || moduleId === "") return;
  try {
    sessionStorage.setItem(
      `${ADD_MODULE_VIEW_MODE_PREFIX}${moduleId}`,
      isViewMode ? "true" : "false",
    );
  } catch {
    /* ignore */
  }
}

/** Max unlink parts to request in one call (live + no module). */
const UNLINK_LIVE_FETCH_LIMIT = "100000";

/** Max concurrent getProcedureRevisionsForPart calls (live + no module Table of Procedures). */
const UNLINK_PART_REVISION_CONCURRENCY = 40;

const getTrainingStatusLabelFromRevision = (revisionOrRow) => {
  const existingLabel = String(revisionOrRow?.trainingStatus || "").trim();
  if (
    existingLabel === "Trained" ||
    existingLabel === "Partially Trained" ||
    existingLabel === "Not Trained"
  ) {
    return existingLabel;
  }

  const raw = String(revisionOrRow?.training_status || "")
    .trim()
    .toLowerCase();
  if (raw === "complete" || raw === "completed") return "Trained";
  if (
    raw === "partial" ||
    raw === "partially_trained" ||
    raw === "partially trained"
  ) {
    return "Partially Trained";
  }
  if (raw === "not_trained" || raw === "not trained") return "Not Trained";

  // Backward compatibility with older payload shape.
  if (revisionOrRow?.is_training_completed === true) return "Trained";
  if (revisionOrRow?.isTrainingCompleted === true) return "Trained";
  return "Not Trained";
};

/**
 * Table of Procedures: Submitter / Approver / Releaser for that revision are shown as Trained (same
 * rule as SearchQuery / training-log SAR rows). If `allRevisionsForPart` is set (from
 * procedure-revision/get-all for the part), also treat the viewer as Trained when they were S/A/R on
 * any older same-letter released revision (e.g. User1 on A1 → Trained on A2 row).
 */
function resolveLiveTableTrainingStatusForViewer(
  revisionOrRow,
  viewerUserId,
  allRevisionsForPart,
) {
  const requiredBy =
    revisionOrRow?.revisionRequiredBy ?? revisionOrRow?.required_by;
  if (
    viewerUserId != null &&
    String(viewerUserId).trim() !== "" &&
    isRevisionWorkflowActor(requiredBy, viewerUserId)
  ) {
    return "Trained";
  }
  if (
    allRevisionsForPart &&
    viewerUserId != null &&
    String(viewerUserId).trim() !== "" &&
    isViewerSarOnOlderSameLetterReleasedRevision(
      allRevisionsForPart,
      revisionOrRow,
      viewerUserId,
    )
  ) {
    return "Trained";
  }
  return getTrainingStatusLabelFromRevision(revisionOrRow);
}

/**
 * Worker pool: run `fn` on each item with at most `poolLimit` in flight. Results match `items` order.
 */
async function asyncPool(poolLimit, items, fn) {
  if (!items.length) return [];
  const results = new Array(items.length);
  let nextIndex = 0;
  async function worker() {
    while (true) {
      const i = nextIndex++;
      if (i >= items.length) return;
      results[i] = await fn(items[i], i);
    }
  }
  const workers = Math.min(poolLimit, items.length);
  await Promise.all(Array.from({ length: workers }, () => worker()));
  return results;
}

/** Avoid `Boolean("false") === true` when navigation state stringifies booleans. */
function normalizeNavIsViewMode(v) {
  if (v === true || v === "true") return true;
  if (v === false || v === "false") return false;
  return Boolean(v);
}

/**
 * Live vs Edit: `location.state` often omits `isViewMode` when returning from Directory
 * (`navigate("/", { replace: true })`), which would reset Live mode — restore from session.
 * @param {{ consumePending?: boolean }} [options] — `false` for useState init (Strict Mode safe peek).
 */
function resolveIsViewModeFromNavigationState(
  locationState,
  currentModuleData,
  options = {},
) {
  const { consumePending = true } = options;
  const st = locationState;
  if (st != null && Object.prototype.hasOwnProperty.call(st, "isViewMode")) {
    try {
      sessionStorage.removeItem(ADD_MODULE_PENDING_DOC_VIEW_MODE);
    } catch {
      /* ignore */
    }
    return normalizeNavIsViewMode(st.isViewMode);
  }
  /** One-shot snapshot from Document back (works when server/nginx drops router state). */
  try {
    const pending = sessionStorage.getItem(ADD_MODULE_PENDING_DOC_VIEW_MODE);
    if (pending === "true" || pending === "false") {
      if (consumePending) {
        sessionStorage.removeItem(ADD_MODULE_PENDING_DOC_VIEW_MODE);
      }
      return pending === "true";
    }
  } catch {
    /* ignore */
  }
  const globalApp = getGlobalAppViewMode();
  if (globalApp !== null) {
    return globalApp;
  }
  const mod = st?.moduleData ?? currentModuleData;
  const mid = mod?.module_id;
  if (mid != null) {
    const persisted = readPersistedAddModuleViewMode(mid);
    if (persisted !== null) return persisted;
  }
  return false;
}

const AddModule = () => {
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [showLoader, setShowLoader] = useState(false);
  const [isRestoringData, setIsRestoringData] = useState(false);
  const hasRestoredData = useRef(false);
  const ignoreNextShortDescChangeRef = useRef(true);
  const shortDescNormalizedRef = useRef("");
  const shortDescBaselineNormalizedRef = useRef("");
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const location = useLocation();

  // Scroll to top whenever this page is navigated to
  useEffect(() => {
    setTimeout(() => {
      window.scrollTo({ top: 0, behavior: "instant" });
      // Defensive cleanup when returning from Preview/routes that may leave this class on body.
      document.body.classList.remove("modal-open");
    }, 100);
  }, [location]);

  const [isUnlink, setIsUnlink] = useState(location.state?.isUnlink || false);
  const [isViewMode, setIsViewMode] = useState(() =>
    resolveIsViewModeFromNavigationState(
      location.state,
      location.state?.moduleData || null,
      { consumePending: false },
    ),
  );

  useEffect(() => {
    if (!isViewMode) {
      setReleasedRevisionRows([]);
    }
  }, [isViewMode]);

  const { unlinkModules, unlkmdlPagination, procedures } = useSelector(
    (state) => state.module,
  );
  // New array reference on every getAllUnlinkModules.fulfilled (.map) re-ran the revision effect → duplicate partRef calls.
  const unlinkModulesStableKey = useMemo(() => {
    const ids = (unlinkModules || [])
      .map((r) => String(r.part_id ?? r.partId ?? ""))
      .filter(Boolean)
      .sort();
    return ids.join("\0");
  }, [unlinkModules]);
  const [moduleData, setModuleData] = useState(
    location.state?.moduleData || null,
  );
  const [hideFromLiveMode, setHideFromLiveMode] = useState(() =>
    getHideFromLiveModeFromModule(location.state?.moduleData),
  );

  // Add state variables for filter dropdown and search
  const [searchType, setSearchType] = useState("all_columns");
  const [selectedColumns, setSelectedColumns] = useState({
    type: true,
    part_number: true,
    short_description: true,
    table_note: true,
    textbox_note: true,
    default_unit: true,
    url: true,
    description: true,
  });

  const [rows, setRows] = useState([]);
  const [sortedAndFiltersRows, setSortedAndFiltersRows] = useState([]);

  const [newPartRow, setNewPartRow] = useState(null);
  const [releasedRevisionRows, setReleasedRevisionRows] = useState([]);
  /** Live + no module (unlink): released revisions are fetched per part — show inline loader while building the table. */
  const [releasedRevisionsLoading, setReleasedRevisionsLoading] =
    useState(false);
  /** Live + assigned module: same while `getProcedureRevisionsForPart` runs for each procedure row. */
  const [moduleReleasedRevisionsLoading, setModuleReleasedRevisionsLoading] =
    useState(false);
  const unlinkLiveReleasedFetchGenRef = useRef(0);
  const [seletctedPart, setSelectedPart] = useState(null);
  const [editRowInfo, setEditRowInfo] = useState({
    editing: false,
    editingIndex: null,
    itemToReplace: null,
  });
  const { editing, editingIndex, itemToReplace } = editRowInfo;
  const [tableSearchQuery, setTableSearchQuery] = useState(
    "recent_item_sort=true",
  );
  const [localSortConfig, setLocalSortConfig] = useState({
    key: null,
    direction: null,
  });

  const [limit, setLimit] = useState(10);
  const [offset, setOffset] = useState(0);
  const [revisionTableModalOpen, setRevisionTableModalOpen] = useState(false);

  // Non-unlink: filter/sort rows for the table.
  useEffect(() => {
    if (isUnlink) return;
    let data = rows;
    if (tableSearchQuery.length > 0) {
      const params = new URLSearchParams(tableSearchQuery);
      let searchType = params.get("search_type");
      searchType = searchType ? searchType.split(",") : [];
      const searchValue = params.get("search");
      if (
        searchValue &&
        searchValue.trim().length > 0 &&
        searchType.length > 0
      ) {
        data = data.filter((item) =>
          searchType.some((key) =>
            item[key]?.toLowerCase().includes(searchValue.toLowerCase()),
          ),
        );
      }
    }

    if (localSortConfig.key !== null) {
      data = SortTableData(
        data,
        localSortConfig.direction,
        localSortConfig.key,
      );
    }
    setSortedAndFiltersRows(data);
  }, [tableSearchQuery, rows, localSortConfig, isUnlink]);

  // Unlink + Live: fetch parts once (local pagination uses limit/offset only in UI).
  useEffect(() => {
    if (!isUnlink || !isViewMode) return;
    const params = new URLSearchParams(tableSearchQuery || "");
    params.set("limit", UNLINK_LIVE_FETCH_LIMIT);
    params.set("offset", "0");
    dispatch(getAllUnlinkModules({ searchQuery: params.toString() }));
  }, [isUnlink, isViewMode, tableSearchQuery, dispatch]);

  // Unlink + Edit: server-side paging for the unlink list.
  useEffect(() => {
    if (!isUnlink || isViewMode) return;
    const params = new URLSearchParams(tableSearchQuery || "");
    params.set("limit", String(limit));
    params.set("offset", String(offset));
    dispatch(getAllUnlinkModules({ searchQuery: params.toString() }));
  }, [isUnlink, isViewMode, tableSearchQuery, dispatch, limit, offset]);

  const closeEditing = () => {
    setEditRowInfo({ editing: false, editingIndex: null, itemToReplace: null });
  };
  const saveSelectedPart = () => {
    if (seletctedPart === null) {
      snackbar("Please select an item code.", "error");
      return;
    }
    setRows((prev) => [{ ...seletctedPart }, ...prev]);
    setNewPartRow(null);
    setSelectedPart(null);
    setHasUnsavedChanges(true);
  };

  // Subscribing to the whole procedureRevision slice re-renders on every getProcedureRevisionsForPart
  // fulfillment during the live unlink table build → AppTable search effect loops. Only subscribe when modal needs data.
  const revisionData = useSelector((state) =>
    revisionTableModalOpen ? state.procedureRevision.revisionData : undefined,
  );
  const { versionHistories } = useSelector((state) => state.versionHistory);
  const authUser = useSelector((state) => state.auth.user);
  const liveModeUserRef = useMemo(() => {
    const fromAuthState = authUser?.id || authUser?.user_id;
    if (fromAuthState) return fromAuthState;
    try {
      const stored = JSON.parse(localStorage.loggedinUserInfo || "{}");
      return stored?.id || stored?.user_id || localStorage.getItem("user_id") || "";
    } catch {
      return localStorage.getItem("user_id") || "";
    }
  }, [authUser?.id, authUser?.user_id]);
  const permissionCtx = buildPermissionContext(authUser);
  const canCreate = permissionCtx.permissions?.Create === true;
  const canEdit = permissionCtx.permissions?.Edit === true;
  const canViewStrict = permissionCtx.permissions?.View === true;
  const allowSave = canEdit;
  const allowPreview = moduleData ? canViewStrict || canEdit : canCreate;
  const areAddModuleProceduresReady = !(
    (isUnlink && releasedRevisionsLoading) ||
    (!isUnlink && moduleReleasedRevisionsLoading)
  );

  const [comment, setComment] = useState(""); // For filtering parts
  const [description, setDescription] = useState(
    moduleData?.module_part?.description || "",
  );

  const [isModalOpen, setModalOpen] = useState(false);
  const [isProjectsModalOpen, setProjectsModalOpen] = useState(false);

  const [formShortDescription, setFormShortDescription] = useState(
    () => convertShortDescToHtml(moduleData?.short_description || ""),
  );

  useEffect(() => {
    const normalized = normalizeShortDescriptionForDirtyCheck(formShortDescription);
    shortDescNormalizedRef.current = normalized;
    shortDescBaselineNormalizedRef.current = normalized;
  }, []);

  const [currentPartInfo, setCurrentPartInfo] = useState({});

  // Version history state
  const [showVersionHistory, setShowVersionHistory] = useState(false);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  // Default to "versions_for_this_page" (Node History) for add module or no module assigned
  const [filterType, setFilterType] = useState("versions_for_this_page");
  const [versionSortConfig, setVersionSortConfig] = useState({
    key: null,
    direction: "ascending",
  });
  const [searchQuery, setSearchQuery] = useState("");

  const [partId, setPartId] = useState(moduleData?.module_part?.part_id || ""); // For modal header
  const [flowchartPartId, setFlowchartPartId] = useState(
    moduleData ? moduleData.flowchart_part_ref : "",
  ); // For modal header

  const [imageFile, setImageFile] = useState(null); // to store file
  const encodedSvg =
    "data:image/png;base64,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";

  const prevImageUrl =
    moduleData?.product_image && moduleData?.product_image.includes("http")
      ? moduleData?.product_image
      : `${BASE}${moduleData?.product_image}`;
  const [previewUrl, setPreviewUrl] = useState(
    isUnlink ? encodedSvg : (moduleData?.product_image && prevImageUrl) || "",
  ); // to show image

  const [procedureNumber, setProcedureNumber] = useState(
    moduleData?.module_part?.part_number || "",
  ); // For modal header
  const [typeName, setTypeName] = useState(
    moduleData?.module_part?.type_name || "",
  );
  const [flowChartProcedureNumber, setFlowchartProcedureNumber] = useState(
    moduleData?.flowchart_part?.part_number || "",
  ); // For modal header

  const snackbar = (message, type) => {
    dispatch(showSnackbar({ type: type, message: message }));
  };

  const [showItemsCodeTable, setShowItemsCodeTable] = useState({
    show: false,
    type: "", // is_module, is_procedure, is_flowchart
  });

  const handleItemsCodePopup = (show, type = "") => {
    setShowItemsCodeTable({ show, type });
  };

  useEffect(() => {
    window.scrollTo(0, 0);
    // Reset overflow styles that may have been set by popups
    document.documentElement.style.overflow = "";
    document.body.style.overflow = "";
    // Ensure stale modal class does not hide Lexical toolbars after navigation.
    document.body.classList.remove("modal-open");
  }, []);

  // Update moduleData and related state when location.state changes
  // This handles the case when navigating to the same route with new data
  useEffect(() => {
    // Skip if we're restoring data from preview
    if (isRestoringData || hasRestoredData.current) {
      return;
    }

    const newModuleData = location.state?.moduleData;
    const newIsUnlink = location.state?.isUnlink ?? false;
    const hasExplicitViewModeInState =
      location.state != null &&
      Object.prototype.hasOwnProperty.call(location.state, "isViewMode");
    let hadPendingDocViewMode = false;
    try {
      const p = sessionStorage.getItem(ADD_MODULE_PENDING_DOC_VIEW_MODE);
      hadPendingDocViewMode = p === "true" || p === "false";
    } catch {
      hadPendingDocViewMode = false;
    }
    const newIsViewMode = resolveIsViewModeFromNavigationState(
      location.state,
      location.state?.moduleData ?? moduleData,
    );

    // Compare module IDs to detect actual changes
    const currentModuleId = moduleData?.module_id;
    const newModuleId = newModuleData?.module_id;
    const moduleDataChanged =
      currentModuleId !== newModuleId ||
      (newModuleData !== undefined && newModuleData !== moduleData);

    // Only update if moduleData actually changed
    if (moduleDataChanged) {
      setModuleData(newModuleData || null);

      // Clear rows when switching to a different module to ensure fresh data
      if (currentModuleId && newModuleId && currentModuleId !== newModuleId) {
        setRows([]);
        setSortedAndFiltersRows([]);
      }

      // Update all dependent state when moduleData changes
      if (newModuleData) {
        setHideFromLiveMode(getHideFromLiveModeFromModule(newModuleData));
        setDescription(newModuleData.module_part?.description || "");
        const cleanedHtml = convertShortDescToHtml(newModuleData.short_description || "");
        // Avoid marking as dirty due to Lexical editor hydration emitting an initial onChange
        ignoreNextShortDescChangeRef.current = true;
        const normalized = normalizeShortDescriptionForDirtyCheck(cleanedHtml);
        shortDescNormalizedRef.current = normalized;
        shortDescBaselineNormalizedRef.current = normalized;
        setFormShortDescription(cleanedHtml);
        setPartId(newModuleData.module_part?.part_id || "");
        setFlowchartPartId(newModuleData.flowchart_part_ref || "");
        setProcedureNumber(newModuleData.module_part?.part_number || "");
        setTypeName(newModuleData.module_part?.type_name || "");
        setFlowchartProcedureNumber(
          newModuleData.flowchart_part?.part_number || "",
        );
        setSelectedProject(newModuleData.project?.product_code || "");
        setSelectedProjectId(newModuleData.project_id_ref || "");

        const prevImageUrl = `${BASE}${newModuleData.product_image}`;
        setPreviewUrl(newModuleData.product_image ? prevImageUrl : "");
      } else {
        // Reset to defaults if moduleData is null
        setHideFromLiveMode(false);
        setDescription("");
        // Avoid marking as dirty due to Lexical editor hydration emitting an initial onChange
        ignoreNextShortDescChangeRef.current = true;
        shortDescNormalizedRef.current = "";
        shortDescBaselineNormalizedRef.current = "";
        setFormShortDescription("");
        setPartId("");
        setFlowchartPartId("");
        setProcedureNumber("");
        setTypeName("");
        setFlowchartProcedureNumber("");
        setSelectedProject("");
        setSelectedProjectId("");
        setPreviewUrl("");
        setRows([]);
        setSortedAndFiltersRows([]);
      }
    }

    // Update isUnlink if it changed
    if (newIsUnlink !== isUnlink) {
      setIsUnlink(newIsUnlink);
    }
    // Sync mode when explicitly provided, when module context changed, or when returning from Document
    // (same route may re-mount with unchanged moduleData but a new Live/Edit choice).
    if (
      hasExplicitViewModeInState ||
      moduleDataChanged ||
      location.state?.fromDocumentBack ||
      hadPendingDocViewMode
    ) {
      setIsViewMode(newIsViewMode);
    }
  }, [location.state, isRestoringData, isUnlink]);

  // Keep Live/Edit preference per module; sync app-wide so Directory / Document / Search match.
  useEffect(() => {
    const id = moduleData?.module_id;
    if (id != null) persistAddModuleViewMode(id, isViewMode);
    setGlobalAppViewMode(isViewMode);
  }, [isViewMode, moduleData?.module_id]);

  // Restore form data when coming back from preview
  useEffect(() => {
    const restoreFormData = () => {
      // Check if we're coming back from preview
      const urlParams = new URLSearchParams(window.location.search);
      const storageKeyFromUrl = urlParams.get("restoreKey");
      let storageKey = storageKeyFromUrl;
      if (!storageKey) {
        try {
          storageKey = sessionStorage.getItem(ADD_MODULE_PREVIEW_RESTORE_KEY);
        } catch {
          storageKey = null;
        }
      }

      if (storageKey) {
        try {
          setIsRestoringData(true);
          const savedData = localStorage.getItem(storageKey);
          if (!savedData) {
            try {
              sessionStorage.removeItem(ADD_MODULE_PREVIEW_RESTORE_KEY);
            } catch {
              /* ignore */
            }
            return;
          }
          if (savedData) {
            const formData = JSON.parse(savedData);

            // Restore all form state
            setSelectedProject(formData.selectedProject || "");
            setSelectedProjectId(formData.selectedProjectId || "");
            setProcedureNumber(formData.procedureNumber || "");
            setPartId(formData.partId || "");
            setFlowchartProcedureNumber(
              formData.flowChartProcedureNumber || "",
            );
            setFlowchartPartId(formData.flowchartPartId || "");
            setDescription(formData.description || "");
            // Avoid marking as dirty due to Lexical editor hydration emitting an initial onChange
            ignoreNextShortDescChangeRef.current = true;
            const cleanedShortDescription = cleanBaselineHtml(formData.formShortDescription || "");
            const normalized = normalizeShortDescriptionForDirtyCheck(
              cleanedShortDescription,
            );
            shortDescNormalizedRef.current = normalized;
            shortDescBaselineNormalizedRef.current = normalized;
            setFormShortDescription(cleanedShortDescription);
            setPreviewUrl(formData.previewUrl || "");

            // Restore rows and sorted rows
            const restoredRows = formData.rows || [];
            setRows(restoredRows);
            setSortedAndFiltersRows(
              formData.sortedAndFiltersRows || restoredRows,
            );

            // Also restore other table-related state
            if (formData.newPartRow !== undefined) {
              setNewPartRow(formData.newPartRow);
            }
            if (formData.seletctedPart !== undefined) {
              setSelectedPart(formData.seletctedPart);
            }
            if (formData.editRowInfo !== undefined) {
              setEditRowInfo(formData.editRowInfo);
            }
            if (formData.tableSearchQuery !== undefined) {
              setTableSearchQuery(formData.tableSearchQuery);
            }
            if (formData.localSortConfig !== undefined) {
              setLocalSortConfig(formData.localSortConfig);
            }

            // Restore moduleData for Edit Module (bookmark and hideFromLiveMode)
            if (formData.moduleData !== undefined) {
              setModuleData(formData.moduleData);
            }
            if (formData.hideFromLiveMode !== undefined) {
              setHideFromLiveMode(formData.hideFromLiveMode);
            } else if (formData.moduleData !== undefined) {
              setHideFromLiveMode(
                getHideFromLiveModeFromModule(formData.moduleData),
              );
            }

            // Restore isUnlink state
            if (formData.isUnlink !== undefined) {
              setIsUnlink(formData.isUnlink);
              // If restoring to isUnlink mode, fetch unlinkModules
              if (formData.isUnlink) {
                dispatch(
                  getAllUnlinkModules({
                    searchQuery: formData.tableSearchQuery || tableSearchQuery,
                  }),
                );
              }
            }

            // Restore pagination state if present
            if (formData.limit !== undefined) {
              setLimit(formData.limit);
            }
            if (formData.offset !== undefined) {
              setOffset(formData.offset);
            }

            // Clean up restore query param but keep key in session for page refresh.
            if (storageKeyFromUrl) {
              const newUrl = window.location.pathname;
              window.history.replaceState({}, document.title, newUrl);
            }
            try {
              sessionStorage.setItem(ADD_MODULE_PREVIEW_RESTORE_KEY, storageKey);
            } catch {
              /* ignore */
            }
            hasRestoredData.current = true;

            // Reset hasUnsavedChanges after restoring data from preview
            setHasUnsavedChanges(Boolean(formData.hasUnsavedChanges));
          }
        } catch (error) {
          console.error("Error restoring form data:", error);
        } finally {
          // Reset the flag after a short delay to allow state updates to complete
          setTimeout(() => {
            setIsRestoringData(false);
          }, 100);
        }
      }
    };

    restoreFormData();
  }, []);

  useEffect(() => {
    // Don't override rows if we're restoring data from preview
    if (isRestoringData || hasRestoredData.current) {
      return;
    }

    if (procedures.length > 0) {
      const transformedRows = procedures.map((row) => {
        // Handle API data format
        if (row.part_id) {
          return {
            partNumber: row.part_number || "",
            type: row.type || "",
            description: row.description || "",
            partId: row.part_id,
            procedureRevisionId: row.procedure_revision_id || "",
            isNew: false,
            isEditing: false,
            hideArrow: false,
          };
        }

        // Handle existing format with procedure_info
        if (row.procedure_info?.part) {
          return {
            partNumber: row.procedure_info.part.part_number || "",
            type: row.procedure_info.part.type?.name || "",
            description: row.procedure_info.part.description || "",
            partId: row.procedure_info.part.id,
            isNew: false,
            isEditing: false,
            hideArrow: false,
          };
        }

        // Return empty row if data structure is unknown
        return {
          partNumber: "",
          type: "",
          description: "",
          partId: "",
          isNew: false,
          isEditing: false,
          hideArrow: false,
        };
      });
      if (location.state === null) {
        setRows([]);
      } else {
        setRows(transformedRows);
        setSortedAndFiltersRows(transformedRows);
      }
    } else {
      setRows([]);
    }
  }, [procedures, location, isRestoringData]);

  // Live + assigned module: released rows from module procedures (sorted/filtered client-side).
  useEffect(() => {
    let isCancelled = false;

    const fetchRevisionsForRow = async (row) => {
      const partId = row.partId ?? row.part_id;
      if (!partId) return [];
      const resp = await dispatch(
        getProcedureRevisionsForPart({
          partId,
          userRef: liveModeUserRef,
          isCheckTrainingCompleted: true,
        }),
      ).unwrap();
      const revisions = Array.isArray(resp?.results) ? resp.results : [];
      return revisions
        .filter((rev) => getCurrentStatus(rev) === "Released")
        .map((rev) => ({
          ...row,
          rev: rev.rev || "-",
          partId: rev.part_ref || partId,
          procedureRevisionId: rev.procedure_revision_id,
          revisionStatus: "Released",
          trainingStatus: getTrainingStatusLabelFromRevision(rev),
          revisionRequiredBy: Array.isArray(rev.required_by)
            ? rev.required_by
            : [],
          allRevisionsForPart: revisions,
        }));
    };

    const run = async () => {
      const sourceRows = sortedAndFiltersRows;
      if (!sourceRows.length) {
        setReleasedRevisionRows([]);
        setModuleReleasedRevisionsLoading(false);
        return;
      }
      setModuleReleasedRevisionsLoading(true);
      try {
        const revisionResponses = await Promise.all(
          sourceRows.map((row) => fetchRevisionsForRow(row)),
        );
        if (isCancelled) return;
        setReleasedRevisionRows(revisionResponses.flat());
      } catch (error) {
        if (isCancelled) return;
        console.error("Error loading released revisions for view mode:", error);
        setReleasedRevisionRows([]);
      } finally {
        if (!isCancelled) setModuleReleasedRevisionsLoading(false);
      }
    };

    if (!isViewMode) {
      setReleasedRevisionRows([]);
      setModuleReleasedRevisionsLoading(false);
    } else if (!isUnlink) {
      run();
    } else {
      setModuleReleasedRevisionsLoading(false);
    }

    return () => {
      isCancelled = true;
    };
  }, [isViewMode, isUnlink, sortedAndFiltersRows, dispatch, liveModeUserRef]);

  // Live + no module: build from unlink list only — do not depend on sortedAndFiltersRows (it churns and retriggered this effect).
  useEffect(() => {
    let isCancelled = false;

    const fetchRevisionsForUnlinkRow = async (row) => {
      const partId = row.partId ?? row.part_id;
      if (!partId) return [];
      const resp = await dispatch(
        getProcedureRevisionsForPart({
          partId,
          userRef: liveModeUserRef,
          isCheckTrainingCompleted: true,
        }),
      ).unwrap();
      const revisions = Array.isArray(resp?.results) ? resp.results : [];
      const normalizedRow = {
        ...row,
        partNumber: row.partNumber ?? row.part_number,
        type: row.type ?? row.type_name,
        partId,
      };
      return revisions
        .filter((rev) => getCurrentStatus(rev) === "Released")
        .map((rev) => ({
          ...normalizedRow,
          rev: rev.rev || "-",
          partId: rev.part_ref || partId,
          procedureRevisionId: rev.procedure_revision_id,
          revisionStatus: "Released",
          trainingStatus: getTrainingStatusLabelFromRevision(rev),
          revisionRequiredBy: Array.isArray(rev.required_by)
            ? rev.required_by
            : [],
          allRevisionsForPart: revisions,
        }));
    };

    const run = async () => {
      if (!isViewMode || !isUnlink) return;
      const sourceRows = unlinkModules || [];
      if (!sourceRows.length) {
        setReleasedRevisionRows([]);
        unlinkLiveReleasedFetchGenRef.current += 1;
        setReleasedRevisionsLoading(false);
        return;
      }
      const fetchGen = ++unlinkLiveReleasedFetchGenRef.current;
      setReleasedRevisionsLoading(true);
      try {
        if (isCancelled) return;
        const rowResults = await asyncPool(
          UNLINK_PART_REVISION_CONCURRENCY,
          sourceRows,
          (row) => fetchRevisionsForUnlinkRow(row),
        );
        if (!isCancelled) setReleasedRevisionRows(rowResults.flat());
      } catch (error) {
        if (isCancelled) return;
        console.error("Error loading released revisions for view mode:", error);
        setReleasedRevisionRows([]);
      } finally {
        if (fetchGen === unlinkLiveReleasedFetchGenRef.current) {
          setReleasedRevisionsLoading(false);
        }
      }
    };

    if (!isViewMode) {
      setReleasedRevisionRows([]);
      unlinkLiveReleasedFetchGenRef.current += 1;
      setReleasedRevisionsLoading(false);
    } else if (isUnlink) {
      run();
    } else {
      unlinkLiveReleasedFetchGenRef.current += 1;
      setReleasedRevisionsLoading(false);
    }

    return () => {
      isCancelled = true;
    };
  }, [isViewMode, isUnlink, unlinkModulesStableKey, dispatch, liveModeUserRef]);

  const releasedRowsForDisplay = useMemo(() => {
    return releasedRevisionRows.map((row) => ({
      ...row,
      trainingStatus: resolveLiveTableTrainingStatusForViewer(
        row,
        liveModeUserRef,
        row.allRevisionsForPart,
      ),
    }));
  }, [releasedRevisionRows, liveModeUserRef]);

  /** Live + no module: paginate the flat released-revision list (offset/limit = rows, not parts). */
  const releasedRowsPagedForDisplay = useMemo(() => {
    if (!isViewMode || !isUnlink) return releasedRowsForDisplay;
    const start = Math.max(0, Number(offset) || 0);
    const size = Math.max(1, Number(limit) || 10);
    return releasedRowsForDisplay.slice(start, start + size);
  }, [releasedRowsForDisplay, isViewMode, isUnlink, offset, limit]);

  // Prevent empty pages when changing page size or when the released row list shrinks.
  useEffect(() => {
    if (!isViewMode || !isUnlink) return;
    const total = releasedRowsForDisplay.length;
    if (total === 0) {
      if (offset !== 0) setOffset(0);
      return;
    }
    if (offset >= total) {
      setOffset(0);
    }
  }, [isViewMode, isUnlink, releasedRowsForDisplay.length, offset, setOffset]);

  const [selectedProject, setSelectedProject] = useState(
    moduleData?.project?.product_code || "",
  );
  const [selectedProjectId, setSelectedProjectId] = useState(
    moduleData ? moduleData.project_id_ref : "",
  ); // Selected project

  useEffect(() => {
    dispatch(getAllProcedureParts({ limit: 100, offset: 0 })); // Fetch all parts for modal
    dispatch(getAllFlowchartParts({ limit: 100, offset: 0 })); // Fetch all parts for modal
    dispatch(getAllModuleParts({ limit: 100, offset: 0 })); // Fetch all parts for modal
    dispatch(getAllTypes({ limit: 1000, offset: 0 }));

    if (moduleData && moduleData.module_id) {
      dispatch(getModuleById({ id: moduleData.module_id }));
    }
  }, [dispatch, isUnlink, moduleData]);

  // Update typeName when moduleData changes
  useEffect(() => {
    if (
      moduleData?.module_part?.type_name &&
      !isRestoringData &&
      !hasRestoredData.current
    ) {
      setTypeName(moduleData.module_part.type_name);
    }
  }, [moduleData?.module_part?.type_name, isRestoringData]);

  // Get projects from Redux state

  const handleAddModule = async (e) => {
    e.preventDefault();

    if (!comment) {
      snackbar("Comment is mandatory.", "error");
      return;
    }
    // Flip loading state immediately so Save UI responds without lag.
    setShowLoader(true);

    // Use local variable for moduleData (don't modify state unnecessarily)
    let currentModuleData = moduleData;
    if (isUnlink) {
      currentModuleData = null;
    }

    // setModalOpen(false);

    const procedureMappingData = [];

    for (const row of rows) {
      // Skip rows without partId
      if (!row.partId) {
        continue;
      }

      // If row already has a procedure revision ID from the API data, use it
      if (row.procedureRevisionId) {
        procedureMappingData.push({
          procedure_revision_ref: row.procedureRevisionId,
        });

        continue;
      }

      // Otherwise fetch revision data
      try {
        const response = await dispatch(
          getRevisionsForPart({ partId: row.partId }),
        ).unwrap();

        const revisions = response?.results[0]?.procedure_revision_data || [];

        if (revisions.length > 0) {
          const latestRevision = [...revisions].sort(
            (a, b) => new Date(b.created_at) - new Date(a.created_at),
          )[0];

          if (latestRevision?.procedure_revision_id) {
            procedureMappingData.push({
              procedure_revision_ref: latestRevision.procedure_revision_id,
            });
          }
        }
      } catch (err) {
        console.error("Error fetching revisions for part:", row.partId, err);
        setShowLoader(false);
        snackbar(`Failed to get revisions for part: ${row.partId}`, "error");
        return;
      }
    }
    const formData = new FormData();
    formData.append("project_id_ref", selectedProjectId || "");
    formData.append("module_part_ref", partId || "");
    formData.append("short_description", formShortDescription);

    // Ensure procedure_mapping_data is properly formatted
    const procedureMappingDataString = JSON.stringify(procedureMappingData);

    formData.append("procedure_mapping_data", procedureMappingDataString);

    if (imageFile) {
      formData.append("product_image", imageFile);
    }

    if (isUnlink) {
      formData.append("is_nomodule", "true");
    }

    if (flowchartPartId) {
      formData.append("flowchart_part_ref", flowchartPartId);
    } else {
      formData.append("flowchart_part_ref", "null");
    }

    formData.append(
      "is_hide_from_live_mode",
      hideFromLiveMode ? "true" : "false",
    );

    let pageTitle = `${typeName} ${procedureNumber}`;
    const auditTrailData = await getAuditTrailData({
      action: currentModuleData ? "MODULE_UPDATION" : "MODULE_CREATION",
      comment: comment,
      parent_version: currentModuleData ? moduleData.version_id_ref : null,
      version_content: {
        action_to_show: currentModuleData
          ? `Updated module ${pageTitle}`
          : `Created module ${pageTitle}`,
        pageTitle,
        selectedProject,
        selectedProjectId,
        procedureNumber,
        partId,
        flowChartProcedureNumber,
        flowchartPartId,
        description,
        short_description: formShortDescription,
        tableOfDocuments: sortedAndFiltersRows,
      },
    });

    if (currentModuleData && !imageFile) {
      auditTrailData.version_content.product_image = moduleData.product_image;
    }

    const new_content_snapshot = await uploadJsonToS3(
      auditTrailData.version_content,
      "MODULE",
      currentModuleData ? "UPDATE" : "CREATE",
      true
    );

    auditTrailData.new_content_snapshot = new_content_snapshot;
    auditTrailData.version_content = {
      action_to_show: auditTrailData.version_content.action_to_show
    };

    // console.log("auditTrailData", auditTrailData);
    // return

    formData.append("audit_trail_data", JSON.stringify(auditTrailData));

    formData.append(
      "exits_image",
      prevImageUrl === previewUrl && currentModuleData
        ? currentModuleData?.product_image
        : null,
    );
    try {
      let resp = {};
      if (currentModuleData) {
        formData.append("module_id", currentModuleData.module_id);
        resp = await dispatch(updateModule(formData)).unwrap();
      } else {
        resp = await dispatch(createModule(formData)).unwrap();
      }
      snackbar(resp.message, resp.success === false ? "error" : "success");
      if (resp.success === false) {
        return;
      }
      dispatch(getAllModules({ limit: 1000, offset: 0 }));
      // Reset unsaved changes before navigation to prevent the blocker from triggering
      setHasUnsavedChanges(false);
      setModalOpen(false);
      try {
        sessionStorage.removeItem(ADD_MODULE_PREVIEW_RESTORE_KEY);
      } catch {
        /* ignore */
      }
      // Navigate directly to home page to avoid history.back interception by the blocker
      navigate("/", { replace: true });
    } catch (error) {
      snackbar(
        `Error ${currentModuleData ? "updating" : "creating"} module: ${error}`,
        "error",
      );
    } finally {
      setShowLoader(false);
    }
  };

  // Handle adding rows
  const addRow = () => {
    setNewPartRow({});
    setHasUnsavedChanges(true);
  };

  // Handle Save Button Click (Open Modal)
  const handleSaveClick = () => {
    if ((selectedProject && partId) || isUnlink) {
      setModalOpen(true);
    } else {
      snackbar("Please fill the mandatory fields", "error");
    }
  };
  // Handle Close Modal
  const handleCloseModal = () => {
    setModalOpen(false);
  };

  const handlePreviewClick = () => {
    // Validate mandatory fields for non-unlink mode
    if (!isUnlink) {
      const missingFields = [];

      // Check Project ID (both display value and ID must be filled)
      const trimmedProject = selectedProject?.trim();
      const trimmedProjectId = selectedProjectId?.trim();
      if (!trimmedProject || !trimmedProjectId) {
        missingFields.push("Project ID");
      }

      // Check Item Code
      const trimmedPartId = partId?.toString().trim();
      if (!trimmedPartId) {
        missingFields.push("Item Code");
      }

      if (missingFields.length > 0) {
        snackbar(
          `Please fill the mandatory fields: ${missingFields.join(", ")}`,
          "error",
        );
        return;
      }
    }

    const formData = {
      selectedProject,
      selectedProjectId,
      procedureNumber,
      partId,
      flowChartProcedureNumber,
      flowchartPartId,
      description,
      formShortDescription,
      previewUrl,
      imageFile: imageFile ? URL.createObjectURL(imageFile) : null, // Convert File to URL for storage
      rows,
      sortedAndFiltersRows,
      newPartRow,
      seletctedPart,
      editRowInfo,
      tableSearchQuery,
      localSortConfig,
      isUnlink,
      isViewMode,
      moduleData,
      limit,
      offset,
      hideFromLiveMode,
      hasUnsavedChanges,
    };

    // Store in localStorage with a unique key
    const storageKey = `addModule_${Date.now()}`;
    localStorage.setItem(storageKey, JSON.stringify(formData));
    try {
      sessionStorage.setItem(ADD_MODULE_PREVIEW_RESTORE_KEY, storageKey);
    } catch {
      /* ignore */
    }

    // Clean up old localStorage entries (keep only last 5)
    const keys = Object.keys(localStorage).filter((key) =>
      key.startsWith("addModule_"),
    );
    if (keys.length > 5) {
      keys
        .sort()
        .slice(0, keys.length - 5)
        .forEach((key) => {
          localStorage.removeItem(key);
        });
    }

    // Collect current form data for preview
    // For isUnlink mode, transform unlinkModules to the format expected by preview
    let previewProcedures = rows;
    if (isUnlink && unlinkModules && unlinkModules.length > 0) {
      previewProcedures = unlinkModules.map((row) => ({
        partNumber: row.part_number || "",
        type: row.type_name || "",
        description: row.description || "",
        partId: row.part_id || "",
      }));
    }

    const previewData = {
      projectId: selectedProject,
      projectIdRef: selectedProjectId,
      itemCode: procedureNumber,
      partId: partId,
      flowchartItemCode: flowChartProcedureNumber,
      flowchartPartId: flowchartPartId,
      description: description,
      shortDescription: formShortDescription,
      imageUrl: previewUrl,
      procedures: previewProcedures,
      isUnlink: isUnlink,
      isViewMode: isViewMode,
      moduleData: moduleData,
      storageKey: storageKey, // Pass the storage key to restore data later
    };

    navigate("/preview", {
      state: {
        previewType: "module",
        modulePreviewData: previewData,
        partNumber: procedureNumber,
        type: typeName,
        revision: "Preview",
      },
    });
  };
  const handleSelectPart = async (selectedPart) => {
    setDescription(selectedPart.description);
    setProcedureNumber(selectedPart.part_number);
    setPartId(selectedPart.part_id);
    setTypeName(selectedPart?.type_details?.name);
    handleItemsCodePopup(false); // Close modal after selection
    setHasUnsavedChanges(true);
  };

  const handleSelectPartForRow = async (selectedPart) => {
    // Validate selected part
    if (!selectedPart || !selectedPart.part_id) {
      snackbar("Invalid part selected", "error");
      return;
    } else if (rows.find((p) => p.partId === selectedPart.part_id)) {
      snackbar("Part is already selected!", "error");
      return;
    }

    let selectedPartInfo = {
      partNumber: selectedPart.part_number || "",
      type: selectedPart.type_details?.name || "",
      description: selectedPart.description || "",
      partId: selectedPart.part_id,
    };

    try {
      // Fetch revisions for the selected part
      const result = await dispatch(
        getRevisionsForPart({
          partId: selectedPart.part_id,
        }),
      ).unwrap();

      // Get revisions from the results array
      const revisions = result?.results || [];

      if (revisions.length < 1) {
        selectedPartInfo = { ...selectedPartInfo, hideArrow: true };
        snackbar("Part has no revisions.", "error");
        return;
      }

      // Get the latest revision
      const latestRevision = [...revisions].sort(
        (a, b) => new Date(b.created_at) - new Date(a.created_at),
      )[0];

      // Store the revision ID in the row data
      selectedPartInfo = {
        ...selectedPartInfo,
        procedureRevisionId: latestRevision.procedure_revision_id,
        revisionNumber: latestRevision.revision_number || "",
      };

      setSelectedPart(selectedPartInfo);
      setEditRowInfo((prev) => ({ ...prev, itemToReplace: selectedPartInfo }));
      handleItemsCodePopup(false);
    } catch (err) {
      console.error("Error fetching revisions:", err);
      snackbar("Failed to fetch revisions for the part.", "error");
    }
  };

  const handleSelectPartForFlowchart = async (selectedPart) => {
    setFlowchartProcedureNumber(selectedPart.part_number);
    handleItemsCodePopup(false);
    setFlowchartPartId(selectedPart.part_id);
    setHasUnsavedChanges(true);
  };

  const handleSelectProject = async (selectedPart) => {
    setSelectedProject(selectedPart.product_code);
    setSelectedProjectId(selectedPart.project_id);
    setProjectsModalOpen(false); // Close modal after selection
    setHasUnsavedChanges(true);
  };

  /** Align Document.js `document_is_view_mode_${revisionId}` before navigate so Live mode is not lost if state hydrates late. */
  const syncDocumentViewModeSessionStorage = useCallback(
    (procedureRevisionId) => {
      if (procedureRevisionId == null || procedureRevisionId === "") return;
      try {
        const v = String(isViewMode);
        sessionStorage.setItem(`document_is_view_mode_${procedureRevisionId}`, v);
        setGlobalAppViewMode(isViewMode);
      } catch {
        /* ignore */
      }
    },
    [isViewMode],
  );

  const handleRevisionSelect = ({
    procedureRevisionId,
    revision,
    status,
    partId,
  }) => {
    // Add fallback for description when undefined or empty
    const description =
      currentPartInfo.description && currentPartInfo.description.length > 0
        ? currentPartInfo.description
        : "No Description";

    const common1 = `${currentPartInfo.type_name || currentPartInfo.type}/${
      currentPartInfo.part_number || currentPartInfo.partNumber
    }/${procedureRevisionId}`;
    const common2 = `${currentPartInfo.type_name || currentPartInfo.type}/${
      currentPartInfo.part_number || currentPartInfo.partNumber
    }/${description}`;

    setRevisionTableModalOpen(false);

    // Prepare navigation state to return to AddModule
    const navigationState = {
      fromAddModule: true,
      moduleData: moduleData,
      isUnlink: isUnlink,
      isViewMode: isViewMode,
    };

    // Store navigation origin in sessionStorage to persist through intermediate navigations
    // Use partId and revisionId to create a unique key
    const storageKey = `navFromAddModule_${partId}_${procedureRevisionId}`;
    try {
      sessionStorage.setItem(
        storageKey,
        JSON.stringify({
          fromAddModule: true,
          moduleData: moduleData,
          isUnlink: isUnlink,
          isViewMode: isViewMode,
        }),
      );
    } catch (error) {
      console.error("Error storing navigation state:", error);
    }

    syncDocumentViewModeSessionStorage(procedureRevisionId);

    switch (status) {
      case "Not Submitted":
        navigate(
          `/revision-submission/${partId}/${revision}/${common1}/${description}`,
          { state: { status: "Not Submitted", ...navigationState } },
        );
        break;
      case "Change Requested":
        navigate(
          `/revision-submission/${partId}/${revision}/${common1}/${description}`,
          { state: { status: "Change Requested", ...navigationState } },
        );
        break;
      case "Submitted For Approval":
        navigate(
          `/approve-revision/${partId}/${revision}/${common1}/${description}`,
          { state: navigationState },
        );
        break;
      case "Approved":
        navigate(
          `/release-revision/${partId}/${revision}/${common1}/${description}`,
          { state: navigationState },
        );
        break;
      case "Released":
        navigate(
          `/released-revision/${partId}/${revision}/${common2}/${procedureRevisionId}`,
          { state: navigationState },
        );
        break;
      case "Rejected":
        navigate(
          `/rejected-revision/${partId}/${revision}/${common2}/Rejected/${procedureRevisionId}`,
          { state: navigationState },
        );
        break;
      case "Archived":
        navigate(
          `/archived-revision/${partId}/${revision}/${common2}/Archived/${procedureRevisionId}`,
          { state: navigationState },
        );
        break;
      default:
    }
  };

  const handleViewModeRevisionSelect = ({
    procedureRevisionId,
    revision,
    partId,
    rowData,
  }) => {
    const description = rowData?.description || "No Description";
    const type = rowData?.type || "";
    const partNumber = rowData?.partNumber || "";
    const common1 = `${type}/${partNumber}/${procedureRevisionId}`;

    syncDocumentViewModeSessionStorage(procedureRevisionId);

    const tableTrainingStatus = rowData?.trainingStatus || "Not Trained";
    const navStorageKey = `navFromAddModule_${partId}_${procedureRevisionId}`;
    try {
      sessionStorage.setItem(
        navStorageKey,
        JSON.stringify({
          fromAddModule: true,
          moduleData: moduleData,
          isUnlink: isUnlink,
          isViewMode: isViewMode,
          /** Mirrors Table of Procedures Training Status when router state is dropped (e.g. refresh). */
          viewerTrainingStatus: tableTrainingStatus,
        }),
      );
    } catch (error) {
      console.error("Error storing navigation state:", error);
    }

    navigate(
      `/revision-submission/${partId}/${revision}/${common1}/${description}`,
      {
        state: {
          status: "Released",
          viewerTrainingStatus: tableTrainingStatus,
          fromAddModule: true,
          moduleData: moduleData,
          isUnlink: isUnlink,
          isViewMode: isViewMode,
        },
      },
    );
  };

  const handleDeleteImage = () => {
    setImageFile(null);
    setPreviewUrl(null);
    setHasUnsavedChanges(true);
  };

  const handleImageUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Reset the file input to allow same file upload next time
    e.target.value = "";

    // Check file size (2MB limit)
    if (file.size > 2 * 1024 * 1024) {
      snackbar("File size exceeds 2MB limit", "error");
      return;
    }

    // Create a URL for the image preview
    const objectUrl = URL.createObjectURL(file);
    setPreviewUrl(objectUrl);
    setImageFile(file);
    setHasUnsavedChanges(true);
  };

  useEffect(() => {
    dispatch(getAllUnits({ limit: 1000, offset: 0 }));
  }, [dispatch]);

  const hasUnsavedShortDescription =
    normalizeShortDescriptionForDirtyCheck(formShortDescription) !==
    shortDescBaselineNormalizedRef.current;
  const hasUnsavedChangesForBlocker =
    hasUnsavedChanges || hasUnsavedShortDescription;

  // Intercept AppBar back navigation
  const handleAppBarBack = useCallback(() => {
    if (hasUnsavedChangesForBlocker) {
      const confirmed = window.confirm(
        "You have unsaved changes. Are you sure you want to leave?",
      );
      if (!confirmed) {
        return;
      }
    }
    try {
      sessionStorage.removeItem(ADD_MODULE_PREVIEW_RESTORE_KEY);
    } catch {
      /* ignore */
    }
    setGlobalAppViewMode(isViewMode);
    // Always navigate to products/directory screen and replace current route
    navigate("/", { replace: true });
  }, [navigate, hasUnsavedChangesForBlocker, isViewMode]);

  useUnsavedChangesBlocker(hasUnsavedChangesForBlocker);

  const switchToEditModeAppbar = () => {
    setIsViewMode(false);
  };

  const switchToViewModeAppbar = () => {
    setIsViewMode(true);
  };

  // Fetch version history when popup opens (only for edit mode)
  useEffect(() => {
    if (
      showVersionHistory &&
      moduleData &&
      moduleData.version_id_ref &&
      !isUnlink
    ) {
      dispatch(
        getAllVersionHistories({ versionId: moduleData.version_id_ref }),
      );
    }
  }, [showVersionHistory, moduleData?.version_id_ref, isUnlink, dispatch]);

  // Sort version history data
  const getSortedVersionHistory = (data) => {
    if (!versionSortConfig.key) return data;
    return [...data].sort((a, b) => {
      let aValue = "";
      let bValue = "";
      switch (versionSortConfig.key) {
        case "date":
          aValue = a.audit_date || "";
          bValue = b.audit_date || "";
          break;
        case "versionId":
          aValue = a.version_id || "";
          bValue = b.version_id || "";
          break;
        case "status":
          aValue = a.action || "";
          bValue = b.action || "";
          break;
        case "modifiedBy": {
          const aUser = `${a.first_name || ""} ${a.last_name || ""}`;
          const bUser = `${b.first_name || ""} ${b.last_name || ""}`;
          aValue = aUser;
          bValue = bUser;
          break;
        }
        case "comment":
          aValue = a.comment || "";
          bValue = b.comment || "";
          break;
        default:
          break;
      }
      aValue = aValue.toLowerCase();
      bValue = bValue.toLowerCase();
      if (aValue < bValue)
        return versionSortConfig.direction === "ascending" ? -1 : 1;
      if (aValue > bValue)
        return versionSortConfig.direction === "ascending" ? 1 : -1;
      return 0;
    });
  };

  // Get app setup state for timezone
  const appSetupState = useSelector((state) => state.appSetup);

  // Format date to app's configured timezone
  const formatDateEST = (dateString) => {
    const { formatDateToAppTimezone } = require("../../util/timezoneUtils");
    return formatDateToAppTimezone(dateString, appSetupState);
  };

  // Export CSV handler
  const handleExportCSV = () => {
    const header = ["Date", "Version ID", "Activity", "Modified by", "Comment"];
    const rows = versionHistories.map((row) => {
      const finalString = formatDateEST(row.audit_date);
      return [
        finalString,
        row.version_id || "-",
        row.action || "-",
        `${row.first_name || ""} ${row.last_name || ""}`.trim() || "-",
        row.comment || "-",
      ];
    });

    const csv = [header, ...rows]
      .map((r) => r.map((v) => `"${v}"`).join(","))
      .join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "version_history.csv";
    a.click();
    window.URL.revokeObjectURL(url);
  };

  // Filter dropdown content
  // Disable "Revision History" filter for add module or no module assigned mode
  const isAddMode = !moduleData || isUnlink;
  const contentBeforeSearchFilter = (
    <FilterDropdown
      isDropdownOpen={isDropdownOpen}
      setIsDropdownOpen={setIsDropdownOpen}
      filterType={filterType}
      setFilterType={setFilterType}
      disabledFltrOptions={isAddMode ? ["all"] : []}
      disableFilter={isAddMode}
      filterOptions={[
        { value: "all", label: "Revision History" },
        { value: "versions_for_this_page", label: "Node History" },
      ]}
    />
  );

  const addModuleAppBarTitle =
    isUnlink
      ? "No module assigned"
      : procedureNumber
        ? typeName
          ? `${typeName} ${procedureNumber}`
          : `Product ${procedureNumber}`
        : "New Module";

  /**
   * Live + no-module: totals match the flat released-revision row count; pages are slices of that list.
   */
  const unlinkPaginationForTable = useMemo(() => {
    if (!isViewMode || !isUnlink) return unlkmdlPagination;
    const total = releasedRowsForDisplay.length;
    const pageSize = Math.max(1, Number(limit) || 10);
    const totalPages = Math.max(1, Math.ceil(total / pageSize));
    return {
      ...unlkmdlPagination,
      visibleRowCount: total,
      total,
      totalPages,
      hideBatchIndicator: true,
    };
  }, [isViewMode, isUnlink, unlkmdlPagination, releasedRowsForDisplay.length, limit]);

  const tableOfDocumentsPagination = useMemo(
    () => ({
      limit: [limit, setLimit],
      offset: [offset, setOffset],
      pagination: unlinkPaginationForTable,
      paginationPosition: {
        position: "center",
      },
      showRecentItems: isUnlink,
    }),
    [limit, offset, unlinkPaginationForTable, isUnlink],
  );

  return (
    <div>
      {isViewMode ? (
        <ProductsLiveModeAppBar
          viewerName={localStorage.getItem("user_name") || ""}
          onEditModeClick={switchToEditModeAppbar}
          showBackButton={true}
          onBack={handleAppBarBack}
          title={addModuleAppBarTitle}
        />
      ) : (
      <AppBar
        title={addModuleAppBarTitle}
        showBookmark={moduleData}
          onBookmarkClick={switchToViewModeAppbar}
        showTablesDropdown={true}
          showHideFromLiveMode={!isUnlink && !isViewMode}
          hideFromLiveMode={hideFromLiveMode}
          onHideFromLiveModeChange={(checked) => {
            setHideFromLiveMode(checked);
            setHasUnsavedChanges(true);
          }}
          disableHideFromLiveMode={!allowSave}
        showQuestionIcon={true}
          questionIconDummy={true}
        showBlackTitle={true}
        onBack={handleAppBarBack}
        buttons={[
          {
            label: "Preview",
            onClick: handlePreviewClick,
            disabled: !allowPreview || !areAddModuleProceduresReady,
            title: !allowPreview
              ? moduleData
                ? "You do not have permission to view this module."
                : "You do not have permission to create modules."
              : !areAddModuleProceduresReady
                ? "Preview will be enabled once all procedures are loaded."
              : undefined,
          },
          {
            label: "Save",
            onClick: canEdit && !isUnlink ? handleSaveClick : undefined,
            disabled: !allowSave || isUnlink,
            title: !allowSave
              ? "You do not have permission to save modules."
              : isUnlink
              ? "Cannot save in 'No module assigned' mode."
              : undefined,
          },
        ]}
        versionHistoryHandler={
          isUnlink || !procedureNumber || moduleData
            ? null
            : () => setShowVersionHistory(true)
        }
      />
      )}
      {/* Module Form */}
      <ModuleForm
        selectedProject={selectedProject}
        procedureNumber={procedureNumber}
        flowChartProcedureNumber={flowChartProcedureNumber}
        description={description}
        formShortDescription={formShortDescription}
        previewUrl={previewUrl}
        onOpenProjectPopup={() => setProjectsModalOpen(true)}
        onOpenItemCodePopup={() => handleItemsCodePopup(true, "is_module")}
        onOpenFlowchartPopup={() => handleItemsCodePopup(true, "is_flowchart")}
        onClearProject={() => {
          if (selectedProject !== "" || selectedProjectId !== "") {
            setHasUnsavedChanges(true);
          }
          setSelectedProject("");
          setSelectedProjectId("");
        }}
        onClearItemCode={() => {
          if (procedureNumber !== "" || partId !== "" || description !== "") {
            setHasUnsavedChanges(true);
          }
          setProcedureNumber("");
          setPartId("");
          setDescription("");
        }}
        onClearFlowchart={() => {
          if (flowChartProcedureNumber !== "" || flowchartPartId !== "") {
            setHasUnsavedChanges(true);
          }
          setFlowchartProcedureNumber("");
          setFlowchartPartId("");
        }}
        onImageUpload={handleImageUpload}
        onDeleteImage={handleDeleteImage}
        onShortDescriptionChange={(value) => {
          // Handle both event objects (from TextArea) and direct values (from ShortDescriptionEditor)
          const newValue =
            typeof value === "object" && value.target ? value.target.value : value;
          
          // Clean the HTML content if it's from the rich text editor
          const cleanedValue = typeof newValue === 'string' && newValue.includes('<') ? 
            cleanBaselineHtml(newValue) : newValue;
          
          const nextNormalized = normalizeShortDescriptionForDirtyCheck(cleanedValue);

          // Lexical frequently emits onChange on mount/hydration even without user edits.
          // Ignore only true no-op hydration updates; if first event is a real user edit,
          // still mark the page dirty.
          if (ignoreNextShortDescChangeRef.current) {
            const currentNormalized = shortDescNormalizedRef.current;
            ignoreNextShortDescChangeRef.current = false;
            shortDescNormalizedRef.current = nextNormalized;
            setFormShortDescription(cleanedValue);
            if (nextNormalized !== currentNormalized) {
              setHasUnsavedChanges(true);
            }
            return;
          }

          const currentNormalized = shortDescNormalizedRef.current;
          setFormShortDescription(cleanedValue);
          shortDescNormalizedRef.current = nextNormalized;
          if (nextNormalized !== currentNormalized) {
            setHasUnsavedChanges(true);
          }
        }}
        isUnlink={isUnlink}
        allowSave={allowSave}
        moduleData={moduleData}
        disabled={isViewMode}
        shortDescriptionHideToolbarWhenReadOnly={isViewMode}
      />
      {/* Live mode (Products): "Table of Procedures"; classic: "Table Of Documents" */}
      <TableOfDocuments
        tableTitle={isViewMode ? "Table of Procedures" : "Table Of Documents"}
        moduleData={moduleData}
        setSearchQuery={setTableSearchQuery}
        setLocalSortConfig={setLocalSortConfig}
        unlinkModules={unlinkModules}
        isUnlink={isUnlink}
        columns={[
          {
            headName: "Item Code",
            field: "partNumber",
            sort: true,
            filter: true,
            required: true,
          },
          { headName: "Type", field: "type", sort: true, filter: true },
          {
            headName: "Description",
            field: "description",
            sort: true,
            filter: true,
          },
          ...(isViewMode
            ? [
                {
                  headName: "Training Status",
                  field: "trainingStatus",
                  sort: false,
                  filter: true,
                },
              ]
            : []),
          ...(isViewMode
            ? [{ headName: "Rev", field: "rev", sort: true, filter: true }]
            : []),
          { headName: "Action", field: "Action", textCenter: true },
        ]}
        pagination={tableOfDocumentsPagination}
      >
        {isViewMode &&
        ((isUnlink && releasedRevisionsLoading) ||
          (!isUnlink && moduleReleasedRevisionsLoading)) ? (
          <tr>
            <td
              colSpan={6}
              style={{
                padding: "32px 16px",
                textAlign: "center",
                verticalAlign: "middle",
              }}
            >
              <InlineLoader text="Loading procedures..." height="100px" />
            </td>
          </tr>
        ) : (
          <>
        {newPartRow && !isViewMode && (
          <tr>
            <td style={{ width: "200px" }}>
              <InputWithPen
                small={true}
                val={seletctedPart?.partNumber}
                onClickOnPencil={() =>
                  handleItemsCodePopup(true, "is_procedure")
                }
                onClear={() => {
                  setSelectedPart(null);
                  setNewPartRow({});
                }}
              />
            </td>
            <td style={{ width: "150px" }}>
              <Input
                placeholder={"Type"}
                value={seletctedPart?.type ? seletctedPart?.type : ""}
              />
            </td>
            <td>
              <RenderHtml
                content={
                  seletctedPart?.description ? seletctedPart?.description : ""
                }
                inptDec={true}
              />
            </td>
            {isViewMode && <td style={{ width: "160px" }}>Not Trained</td>}
            <td style={{ width: "100px" }}>
              <TableActionGroup
                mode="editing"
                onSave={saveSelectedPart}
                onCancel={() => {
                  setSelectedPart(null);
                  setNewPartRow(null);
                }}
              />
            </td>
          </tr>
        )}
        {isUnlink && !isViewMode
          ? unlinkModules.map((row, index) => (
              <tr key={index}>
                <td style={{ width: "200px" }}>{row.part_number}</td>
                <td style={{ width: "150px" }}>{row.type_name}</td>
                <td>
                  <RenderHtml content={row.description} />
                </td>
                <td style={{ width: "100px" }}>
                  <TableActionGroup
                    mode="view"
                    showEdit={true}
                    showDelete={true}
                    showSelect={true}
                    disableEdit={true}
                    disableDelete={true}
                    disableSelect={row.hideArrow || false}
                    onSelect={async () => {
                      setCurrentPartInfo(row);
                      await dispatch(
                        getProcedureRevisionsForPart({
                          partId: row.part_id,
                          userRef: liveModeUserRef,
                          isCheckTrainingCompleted: true,
                        }),
                      );
                      setRevisionTableModalOpen(true);
                    }}
                  />
                </td>
              </tr>
            ))
          : ((isViewMode && isUnlink) ? releasedRowsPagedForDisplay : (isViewMode ? releasedRowsForDisplay : sortedAndFiltersRows)).map(
              (row, index) => {
              const editCurrentRow =
                editing && editingIndex === index ? true : false;
              return (
                <tr key={index}>
                  <td className="text-left w-200">
                    {editCurrentRow ? (
                      <>
                        <InputWithPen
                          small={true}
                          val={itemToReplace.partNumber}
                          onClickOnPencil={() =>
                            handleItemsCodePopup(true, "is_procedure")
                          }
                          onClear={() => {
                            setEditRowInfo((prev) => ({
                              ...prev,
                              itemToReplace: { type: "" },
                            }));
                          }}
                        />
                      </>
                    ) : (
                      row.partNumber
                    )}
                  </td>
                  <td className="w-150">
                    {editCurrentRow ? (
                      <input
                        type="text"
                        className="edit-inline-input"
                        value={itemToReplace.type}
                        placeholder="Type"
                        readOnly
                      />
                    ) : (
                      row.type
                    )}
                  </td>
                  <td>
                    {isViewMode ? (
                      <RenderHtml content={row.description} />
                    ) : editCurrentRow ? (
                      <RenderHtml
                        content={itemToReplace.description}
                        inptDec={true}
                      />
                    ) : (
                      <RenderHtml content={row.description} />
                    )}
                  </td>
                  {isViewMode && (
                    <td className="w-150">{row.trainingStatus}</td>
                  )}
                  {isViewMode && <td className="w-100">{row.rev || "-"}</td>}

                  <td className="w-100">
                    {isViewMode ? (
                      <TableActionGroup
                        mode="view"
                        showEdit={false}
                        showDelete={false}
                        showSelect={true}
                        disableSelect={!row.procedureRevisionId}
                        onSelect={() =>
                          handleViewModeRevisionSelect({
                            procedureRevisionId: row.procedureRevisionId,
                            revision: row.rev,
                            partId: row.partId,
                            rowData: row,
                          })
                        }
                      />
                    ) : editCurrentRow ? (
                      <TableActionGroup
                        mode="editing"
                        onSave={() => {
                          if (!itemToReplace.partNumber) {
                            snackbar("Please select an item code.", "error");
                            return;
                          }
                          setRows((prev) => {
                            const updt = structuredClone(prev);
                            updt[editingIndex] = itemToReplace;
                            return updt;
                          });
                          setHasUnsavedChanges(true);
                          closeEditing();
                        }}
                        onCancel={closeEditing}
                      />
                    ) : (
                      <TableActionGroup
                        mode="view"
                        showEdit={!isViewMode}
                        showDelete={!isViewMode}
                        showSelect={true}
                        disableEdit={newPartRow}
                        disableDelete={editing}
                        disableSelect={row.hideArrow || false}
                        onEdit={() =>
                          setEditRowInfo({
                            editing: true,
                            editingIndex: index,
                            itemToReplace: row,
                          })
                        }
                        onDelete={() =>
                          {
                            setRows(rows.filter((_, i) => i !== index));
                            setHasUnsavedChanges(true);
                          }
                        }
                        onSelect={async () => {
                          await dispatch(
                            getProcedureRevisionsForPart({
                              partId: row.partId,
                              userRef: liveModeUserRef,
                              isCheckTrainingCompleted: true,
                            }),
                          );
                          setCurrentPartInfo(row);
                          setRevisionTableModalOpen(true);
                        }}
                      />
                    )}
                  </td>
                </tr>
              );
            })}
          </>
        )}
      </TableOfDocuments>

      {!isUnlink && !isViewMode && (
        <>
          <button
            onClick={addRow}
            disabled={newPartRow !== null || editing || !allowSave}
            className="add-row-button"
            title={
              !allowSave ? "You do not have permission to add rows." : undefined
            }
            style={{
              opacity: !allowSave ? 0.5 : 1,
              cursor: !allowSave ? "not-allowed" : "pointer",
            }}
          >
            + Add Row
          </button>
        </>
      )}

      <br />

      {/* Project Modal */}
      {isProjectsModalOpen && (
        <>
          <ProjectIdPopup
            onClose={setProjectsModalOpen}
            snackbar={snackbar}
            handleSelectProject={handleSelectProject}
          />
        </>
      )}

      {/* Parts Modal */}
      {showItemsCodeTable.show && (
        <PartsPopup
          setPartsModalOpen={() => {
            handleItemsCodePopup(false);
          }}
          handleSelectPart={(part) => {
            switch (showItemsCodeTable.type) {
              case "is_module":
                handleSelectPart(part);
                break;
              case "is_procedure":
                handleSelectPartForRow(part);
                break;
              case "is_flowchart":
                handleSelectPartForFlowchart(part);
                break;
              default:
            }
          }}
          items_type={showItemsCodeTable.type}
          check_module_created={
            showItemsCodeTable.type === "is_module" ? true : false
          }
          checkRevCreatedNot={
            showItemsCodeTable.type === "is_procedure" ? true : false
          }
        />
      )}

      {/* Select Revision Modal */}
      <SelectRevisionModal
        isOpen={revisionTableModalOpen}
        onClose={() => setRevisionTableModalOpen(false)}
        revisionData={revisionData}
        currentPartInfo={currentPartInfo}
        onRevisionSelect={handleRevisionSelect}
      />

      <SaveModal
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        onSave={handleAddModule}
        title={`Save ${typeName || ""} ${procedureNumber || ""}`}
        subtitle={undefined}
        editorName={localStorage.getItem("user_name") || "Unknown User"}
        comment={comment}
        onCommentChange={(e) => {
          const next = e.target.value;
          setComment(next);
          if (next !== comment) {
            setHasUnsavedChanges(true);
          }
        }}
        isLoading={showLoader}
        showLoader={true}
      />

      {showLoader && <Loader />}

      {/* Version History Popup */}
      {showVersionHistory && (
        <Popup
          title="Version History"
          closeModal={() => setShowVersionHistory(false)}
        >
          <div
            style={{
              minWidth: 900,
              maxWidth: 1480,
              paddingLeft: 16,
              paddingRight: 16,
            }}
          >
            <div style={{ maxHeight: 400, overflowY: "auto" }}>
              <AppTable
                columnsData={[
                  {
                    headName: "Date",
                    field: "audit_date",
                    sort: true,
                    filter: true,
                    width: "15%",
                  },
                  {
                    headName: "Version ID",
                    field: "version_id",
                    sort: true,
                    filter: true,
                    width: "30%",
                  },
                  {
                    headName: "Activity",
                    field: "action",
                    sort: true,
                    filter: true,
                    width: "15%",
                  },
                  {
                    headName: "Modified by",
                    field: "modified_by",
                    sort: true,
                    filter: true,
                    width: "15%",
                  },
                  {
                    headName: "Comment",
                    field: "comment",
                    sort: true,
                    filter: true,
                    width: "20%",
                  },
                  { headName: "Action", field: "action_button", width: "5%" },
                ]}
                showSearchInput={true}
                contentBeforeSearchFilter={contentBeforeSearchFilter}
                usingInPopup={true}
                extrapadding="0px"
                onFilterSortHandler={(queryString) => {
                  const params = new URLSearchParams(queryString);
                  const search = params.get("search") || "";
                  setSearchQuery(search);
                }}
              >
                {!moduleData || isUnlink ? (
                  <tr>
                    <td
                      colSpan="6"
                      style={{ textAlign: "center", padding: "20px" }}
                    >
                      No version history available
                    </td>
                  </tr>
                ) : (
                  getSortedVersionHistory(versionHistories)
                    .filter((row) => {
                      // Apply filter type
                      if (filterType === "all") {
                        // Revision History - show all versions
                      } else if (filterType === "versions_for_this_page") {
                        // For Node History, filter by module_id
                        const currentModuleId = moduleData?.module_id;
                        const rowModuleId =
                          row.version_content?.module_id ||
                          row.parent_version_data?.version_content?.module_id;

                        if (
                          !currentModuleId ||
                          rowModuleId !== currentModuleId
                        ) {
                          return false;
                        }
                      }

                      // Apply client-side search filtering
                      if (searchQuery) {
                        const query = searchQuery.toLowerCase();
                        const searchableText = [
                          row.version_id || "",
                          row.action || "",
                          `${row.first_name || ""} ${row.last_name || ""}`,
                          row.comment || "",
                          row.version_content?.breadcrumb_text ||
                            row.parent_version_data?.version_content
                              ?.breadcrumb_text ||
                            "",
                        ]
                          .join(" ")
                          .toLowerCase();

                        return searchableText.includes(query);
                      }

                      return true;
                    })
                    .map((row, idx) => {
                      const finalString = formatDateEST(row.audit_date);

                      return (
                        <tr key={idx}>
                          <td style={{ textAlign: "left" }}>{finalString}</td>
                          <td>
                            {moduleData?.module_part?.part_number &&
                              `Product ${moduleData.module_part.part_number}`}
                            {(row.version_content?.breadcrumb_text ||
                              row.parent_version_data?.version_content
                                ?.breadcrumb_text) &&
                              ` > ${
                                row.version_content?.breadcrumb_text ||
                                row.parent_version_data?.version_content
                                  ?.breadcrumb_text
                              }`}{" "}
                            &gt; Version {row.version_id}
                          </td>
                          <td>{row.action || ""}</td>
                          <td>
                            <div
                              style={{
                                display: "flex",
                                alignItems: "center",
                                gap: "8px",
                              }}
                            >
                              <img
                                src={Images.Profile}
                                alt="Profile"
                                style={{
                                  width: "20px",
                                  height: "20px",
                                  borderRadius: "50%",
                                  objectFit: "cover",
                                }}
                              />
                              <span>
                                {row.first_name || ""} {row.last_name || ""}
                              </span>
                            </div>
                          </td>
                          <td>{row.comment || "-"}</td>
                          <td>
                            <button
                              className="right-arrow"
                              onClick={() => {}}
                              disabled={!canViewStrict}
                              style={{
                                cursor: !canViewStrict
                                  ? "not-allowed"
                                  : "pointer",
                                opacity: !canViewStrict ? 0.5 : 1,
                              }}
                            >
                              <FontAwesomeIcon icon={faArrowRight} />
                            </button>
                          </td>
                        </tr>
                      );
                    })
                )}
              </AppTable>
            </div>

            <div
              style={{
                display: "flex",
                justifyContent: "flex-end",
                marginTop: 16,
                marginBottom: 16,
                marginRight: 16,
              }}
            >
              <ExportCsvBtn handleExportCSV={handleExportCSV} />
            </div>
          </div>
        </Popup>
      )}
    </div>
  );
};

export default AddModule;
